import React, { useEffect, useState } from "react";
import {
  colors2,
  Tooltip,
  UI_BODY_SM,
  UI_BODY_SEMIBOLD_SM,
  UI_CAPTION,
} from "oolib";
import {
  ButtonPrimaryCompact,
  ButtonTertiaryCompact,
} from "oolib/dist/v2/components/Buttons";
import { DropdownSingle } from "oolib/dist/v2/components/Dropdowns/DropdownSingle";
import { DisplayIcon } from "oolib/dist/utils/comps/DisplayIcon";
import { Card, CardHeader, Row, Actions } from "./styled";
import { Dot, FREQUENCIES } from "./common";

// "Off" leaves the dataset one-time: no schedule.
const SCHEDULE_OPTIONS = [{ display: "Off", value: "off" }, ...FREQUENCIES];

/**
 * Sync Now, disabled with its reason as a tooltip when the dataset can't sync
 * yet. The title on the wrapping span keeps the reason reachable even if the
 * disabled button swallows hover.
 */
export const SyncNowButton = ({ dataset, onSyncNow }) => {
  const button = (
    <ButtonTertiaryCompact
      icon="ArrowsClockwise"
      disabled={!!dataset.syncBlockedReason || dataset.syncing}
      onClick={() => onSyncNow(dataset)}
    >
      {dataset.syncing ? "Syncing…" : "Sync Now"}
    </ButtonTertiaryCompact>
  );
  return dataset.syncBlockedReason ? (
    <Tooltip text={dataset.syncBlockedReason} position="left">
      <span title={dataset.syncBlockedReason}>{button}</span>
    </Tooltip>
  ) : (
    button
  );
};

/**
 * One dataset's sync card in a manage popup: status, Sync Now, the auto-sync
 * frequency, next and last sync. Without canSync it only shows the last sync;
 * without canPublish Sync Now is hidden and the frequency is read-only.
 */
export const SyncCard = ({
  dataset,
  canSync,
  canPublish,
  justSaved,
  onSyncNow,
  onApplyFrequency,
}) => {
  const scheduled = dataset.schedule !== "one-time";
  const current =
    SCHEDULE_OPTIONS.find((f) => f.value === dataset.frequency) ||
    SCHEDULE_OPTIONS[0];
  // Picked but not applied yet; dropped once the saved schedule matches it.
  const [pending, setPending] = useState(null);
  const shown = pending || current;
  const hasPendingChange = !!pending && pending.value !== current.value;
  useEffect(() => {
    if (pending && pending.value === current.value) setPending(null);
  }, [pending, current.value]);

  // Brief "Saved" after the schedule saves.
  const [showSaved, setShowSaved] = useState(false);
  useEffect(() => {
    if (!justSaved) return;
    setShowSaved(true);
    const t = setTimeout(() => setShowSaved(false), 2000);
    return () => clearTimeout(t);
  }, [justSaved]);

  const { lastSync } = dataset;

  return (
    <Card>
      {canSync && (
        <CardHeader>
          <Actions style={{ gap: 6 }}>
            {scheduled && (
              <DisplayIcon icon="CheckCircle" size={18} color={colors2.green} />
            )}
            <UI_BODY_SEMIBOLD_SM
              style={{ color: scheduled ? colors2.greenText : colors2.grey60 }}
            >
              {scheduled ? "Sync active" : "Auto-sync off"}
            </UI_BODY_SEMIBOLD_SM>
          </Actions>
          {canPublish && (
            <SyncNowButton dataset={dataset} onSyncNow={onSyncNow} />
          )}
        </CardHeader>
      )}
      {/* Explains a schedule reading "Off" that the user did not switch off. */}
      {dataset.autoPausedReason && (
        <Row
          style={{
            justifyContent: "flex-start",
            background: colors2.yellowBG || "#fff8e1",
          }}
        >
          <DisplayIcon icon="WarningCircle" size={16} color="#d99100" />
          <UI_CAPTION style={{ color: colors2.grey80 }}>
            {`Auto-sync was switched off after ${dataset.autoPausedReason}. `}
            {"Fix the cause, then set a schedule again below."}
          </UI_CAPTION>
        </Row>
      )}
      {canSync && (
        <Row>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <UI_BODY_SM>Auto-sync</UI_BODY_SM>
              {dataset.savingSchedule && (
                <UI_CAPTION style={{ color: colors2.grey60 }}>
                  Saving…
                </UI_CAPTION>
              )}
              {!dataset.savingSchedule && showSaved && (
                <UI_CAPTION style={{ color: colors2.greenText }}>
                  Saved
                </UI_CAPTION>
              )}
            </div>
            <UI_BODY_SM style={{ color: colors2.grey60 }}>
              Automatically pull new data on a schedule.
            </UI_BODY_SM>
          </div>
          <Actions>
            <DropdownSingle
              id="frequency"
              popOutOfOverflowHiddenParent
              options={SCHEDULE_OPTIONS}
              value={shown}
              onChange={
                canPublish ? (_k, op) => op && setPending(op) : undefined
              }
              readOnly={!canPublish}
              genCustomSelectComp={() => (
                <ButtonTertiaryCompact iconAfter="CaretDown">
                  {shown.display}
                </ButtonTertiaryCompact>
              )}
            />
            {canPublish && (
              <ButtonPrimaryCompact
                disabled={!hasPendingChange || dataset.savingSchedule}
                onClick={() => onApplyFrequency(dataset, pending.value)}
              >
                Apply
              </ButtonPrimaryCompact>
            )}
          </Actions>
        </Row>
      )}
      {dataset.nextSync && (
        <Row>
          <UI_BODY_SM style={{ color: colors2.grey60 }}>
            Next auto-sync
          </UI_BODY_SM>
          <UI_BODY_SM>{dataset.nextSync}</UI_BODY_SM>
        </Row>
      )}
      <Row>
        <UI_BODY_SM style={{ color: colors2.grey60 }}>Last sync</UI_BODY_SM>
        <UI_BODY_SM>
          {lastSync ? (
            <>
              {lastSync.at}
              {lastSync.rows !== null && (
                <>
                  <Dot />
                  {`${lastSync.rows} rows`}
                </>
              )}
              {lastSync.source && (
                <>
                  <Dot />
                  {lastSync.source}
                </>
              )}
            </>
          ) : (
            "No imports yet"
          )}
        </UI_BODY_SM>
      </Row>
    </Card>
  );
};

export default SyncCard;
